import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  IconButton, Collapse, Button, Pagination, ToggleButton, ToggleButtonGroup, Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { sendServerRequest } from '../../../server/request';
import { openGlobalSnack } from '../../utils/snack';
import { CircularLoading } from '../../utils/loading';
import { fmtDate, prettyJson, jsonPreStyle } from './historyutils';

const PAGE_SIZE = 20;

type Provider = 'soma' | 'v8';

type SomaConsult = {
  id: number; con_id: number; chat_id: number | null; cpf: string; nome: string;
  celular: string; bancalizadora: string; status_nome: string; parceiro: string;
  criado_por: string; result: string | null; createdAt: string;
};
type V8Consult = {
  id: number; cpf: string; consult_id: string | null; chat_id: number | null;
  client_id: number | null; is_active: boolean; status: string | null; createdAt: string;
};
type Row = (SomaConsult | V8Consult) & { id: number };

const ENDPOINTS: Record<Provider, string> = {
  soma: '/server/cltCommand/somaConsults',
  v8: '/server/cltCommand/v8Consults',
};

export function CltConsultsPanel() {
  const [provider, setProvider] = useState<Provider>('soma');
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    load(provider, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  function load(prov: Provider, toPage: number) {
    setLoading(true);
    setExpanded(null);
    const q = new URLSearchParams({ page: String(toPage), pageSize: String(PAGE_SIZE) });
    if (cpf.trim()) q.set('cpf', cpf.trim());
    sendServerRequest('GET', `${ENDPOINTS[prov]}/list?${q.toString()}`)
      .then((e) => {
        const data = e.data?.data ?? {};
        setRows(data.consults ?? []);
        setTotal(data.total ?? 0);
        setPage(toPage);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar o histórico de consultas.', 'error');
      });
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isSoma = provider === 'soma';

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <h3 style={{ marginBottom: 4, textAlign: 'center' }}>Histórico de consultas</h3>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
        <ToggleButtonGroup size="small" exclusive value={provider}
          onChange={(_, v) => v && setProvider(v)}>
          <ToggleButton value="soma">Soma</ToggleButton>
          <ToggleButton value="v8">V8</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" label="CPF" value={cpf} onChange={(e) => setCpf(e.target.value)} />
        <Button variant="contained" onClick={() => load(provider, 1)}>Filtrar</Button>
        <IconButton onClick={() => load(provider, page)}><RefreshIcon /></IconButton>
      </div>

      {loading ? <CircularLoading /> : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Data</TableCell>
                <TableCell>CPF</TableCell>
                {isSoma ? <>
                  <TableCell>Nome</TableCell>
                  <TableCell>Bancalizadora</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>conId</TableCell>
                </> : <>
                  <TableCell>consult_id</TableCell>
                  <TableCell>chat_id</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ativo</TableCell>
                </>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" style={{ color: '#00000080' }}>Nenhuma consulta.</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                        {expanded === r.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.createdAt)}</TableCell>
                    <TableCell>{r.cpf ?? '—'}</TableCell>
                    {isSoma ? <>
                      <TableCell>{(r as SomaConsult).nome ?? '—'}</TableCell>
                      <TableCell>{(r as SomaConsult).bancalizadora ?? '—'}</TableCell>
                      <TableCell>{(r as SomaConsult).status_nome ?? '—'}</TableCell>
                      <TableCell>{(r as SomaConsult).con_id ?? '—'}</TableCell>
                    </> : <>
                      <TableCell>{(r as V8Consult).consult_id ?? '—'}</TableCell>
                      <TableCell>{(r as V8Consult).chat_id ?? '—'}</TableCell>
                      <TableCell>{(r as V8Consult).status ?? '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined"
                          color={(r as V8Consult).is_active ? 'success' : 'default'}
                          label={(r as V8Consult).is_active ? 'Sim' : 'Não'} />
                      </TableCell>
                    </>}
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} style={{ paddingTop: 0, paddingBottom: 0, border: 0 }}>
                      <Collapse in={expanded === r.id} unmountOnExit>
                        <pre style={{ ...jsonPreStyle, margin: '8px 0' }}>
                          {isSoma
                            ? (prettyJson((r as SomaConsult).result) || JSON.stringify(r, null, 2))
                            : JSON.stringify(r, null, 2)}
                        </pre>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
          <Pagination count={pages} page={page} onChange={(_, p) => load(provider, p)} />
        </div>
      )}
    </div>
  );
}
